'use client';

/**
 * CoinBalanceProvider
 *
 * Provides initial coin balance data from server-side loading.
 * This enables React Query to use initialData, avoiding loading spinners.
 *
 * The balance is:
 * - Loaded server-side via loadUserWorkspace()
 * - Passed to this provider via props
 * - Consumed by useCoinBalance() hook for instant display
 * - Updated via invalidateBalance() after spend/purchase
 */
import { ReactNode, createContext, useContext } from 'react';

export interface CoinBalanceData {
  coins: number;
  totalEarned: number;
  totalSpent: number;
}

const CoinBalanceContext = createContext<CoinBalanceData | null>(null);

interface CoinBalanceProviderProps {
  children: ReactNode;
  initialBalance?: CoinBalanceData;
}

export function CoinBalanceProvider({
  children,
  initialBalance,
}: CoinBalanceProviderProps) {
  return (
    <CoinBalanceContext.Provider value={initialBalance ?? null}>
      {children}
    </CoinBalanceContext.Provider>
  );
}

/**
 * Hook to get the initial coin balance from server-side loading.
 * Returns null if not within a CoinBalanceProvider.
 */
export function useInitialCoinBalance(): CoinBalanceData | null {
  return useContext(CoinBalanceContext);
}
